import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, AuthContextType } from '../types';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Web storage helper
const storage = {
  getItem: (key: string): string | null => {
    return localStorage.getItem(key);
  },
  setItem: (key: string, value: string): void => {
    localStorage.setItem(key, value);
  },
  removeItem: (key: string): void => {
    localStorage.removeItem(key);
  },
  clear: (): void => {
    localStorage.clear();
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Function to reset auth state (used by login page after logout)
  const resetAuthState = () => {
    setUser(null);
    setToken(null);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check for session_id in URL (Google OAuth callback)
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id') || window.location.hash.match(/session_id=([^&]+)/)?.[1];
      
      if (sessionId) {
        await processGoogleSession(sessionId);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      // Check stored token
      const storedToken = storage.getItem('token');
      if (storedToken) {
        setToken(storedToken);
        await fetchUser(storedToken);
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const processGoogleSession = async (sessionId: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        storage.setItem('token', sessionId);
        setToken(sessionId);
      }
    } catch (error) {
      console.error('Google session error:', error);
    }
  };

  const fetchUser = async (authToken: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        storage.removeItem('token');
        setToken(null);
      }
    } catch (error) {
      console.error('Fetch user error:', error);
    }
  };

  const login = async (email: string, password: string) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();

    if (data.requires_2fa) {
      return { requires2FA: true, userId: data.user_id };
    }

    storage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return {};
  };

  const register = async (email: string, password: string, name: string, phoneNumber?: string) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, phone_number: phoneNumber })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await response.json();
    storage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
  };

  const verify2FA = async (userId: string, code: string) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/verify-2fa?user_id=${userId}&code=${code}`, {
      method: 'POST'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Invalid 2FA code');
    }

    const data = await response.json();
    storage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
  };

  const loginWithGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + '/auth/google/callback';
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    if (!clientId) {
      throw new Error('Google OAuth not configured');
    }
    
    // Build Google OAuth URL
    const googleAuthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    googleAuthUrl.searchParams.set('client_id', clientId);
    googleAuthUrl.searchParams.set('redirect_uri', redirectUri);
    googleAuthUrl.searchParams.set('response_type', 'code');
    googleAuthUrl.searchParams.set('scope', 'openid email profile');
    googleAuthUrl.searchParams.set('access_type', 'offline');
    googleAuthUrl.searchParams.set('prompt', 'select_account');
    
    window.location.href = googleAuthUrl.toString();
  };

  const logout = async () => {
    console.log('Logout: Starting logout process...');
    
    // Clear state FIRST before any async operations
    setToken(null);
    setUser(null);
    
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include'
      });
      console.log('Logout: Server logout call completed');
    } catch (error) {
      console.error('Logout: Server call error:', error);
    }
    
    // Clear token from storage
    try {
      storage.clear();
      console.log('Logout: Token removed from storage');
    } catch (error) {
      console.error('Logout: Storage error:', error);
    }
    
    console.log('Logout: State cleared - user and token set to null');
    
    // Navigate to login page
    window.location.replace('/login');
  };

  const updateUser = async (updates: Partial<User>) => {
    if (!token) return;

    const response = await fetch(`${BACKEND_URL}/api/users/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });

    if (response.ok) {
      const updatedUser = await response.json();
      setUser(updatedUser);
    }
  };

  // Set auth state directly (used by Google OAuth callback)
  const setAuthState = (userData: User, authToken: string) => {
    storage.setItem('token', authToken);
    setToken(authToken);
    setUser(userData);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        login,
        register,
        verify2FA,
        loginWithGoogle,
        logout,
        updateUser,
        resetAuthState,
        setAuthState
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
